import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

export const extractDate = (date: string | Date | undefined | null): string => {
  if (!date) return 'N/A';
  const parsedDate = dayjs(date);
  return parsedDate.isValid()
    ? parsedDate.format('DD/MM/YYYY')
    : 'Fecha inválida';
};

export const extractTime = (date: string | Date | undefined | null): string => {
  if (!date) return 'N/A';
  const parsedDate = dayjs(date);
  return parsedDate.isValid() ? parsedDate.format('HH:mm') : 'Fecha inválida';
};

export const convertMinutesToHours = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = (minutes % 60).toFixed(0);
  return `${hours}:${remainingMinutes.toString().padStart(2, '0')}`;
};

export const formatDurationMinutesAsHoursAndMinutes = (
  totalMinutes: number
): string => {
  if (!Number.isFinite(totalMinutes) || totalMinutes < 0) {
    return '0min';
  }
  const rounded = Math.round(totalMinutes);
  const hours = Math.floor(rounded / 60);
  const minutes = rounded % 60;
  if (hours === 0) {
    return `${minutes}min`;
  }
  if (minutes === 0) {
    return `${hours}h`;
  }
  return `${hours}h ${minutes}min`;
};

export const timeAgo = (date: string) => {
  const parsedDate = dayjs(date);
  return parsedDate.fromNow();
};

export const formatDate = (date: string | Date | undefined | null): string => {
  if (!date) return 'N/A';
  const parsedDate = dayjs(date);
  return parsedDate.isValid()
    ? parsedDate.format('DD/MM/YYYY HH:mm')
    : 'Fecha inválida';
};

export const formatRelativeTime = (dateString?: string): string => {
  if (!dateString) return 'No disponible';
  const date = dayjs(dateString);
  if (!date.isValid()) return 'Fecha inválida';

  const diffDays = dayjs().diff(date, 'day');

  if (diffDays >= 7) {
    return date.format('DD/MM/YYYY HH:mm');
  }

  return date.fromNow();
};

export const startOfLocalDay = (date: Date | null): Date | null =>
  date ? dayjs(date).startOf('day').toDate() : null;

export const endOfLocalDay = (date: Date | null): Date | null =>
  date ? dayjs(date).endOf('day').toDate() : null;

export type DayBoundary = 'start' | 'end';

export function mergeCalendarDateKeepingTime(
  calendarDate: Date | null,
  previous: Date | null,
  emptyPreviousDefault: DayBoundary
): Date | null {
  if (!calendarDate) return null;
  const next = dayjs(calendarDate);
  if (previous) {
    const prev = dayjs(previous);
    return next
      .hour(prev.hour())
      .minute(prev.minute())
      .second(prev.second())
      .millisecond(prev.millisecond())
      .toDate();
  }
  return emptyPreviousDefault === 'start'
    ? next.startOf('day').toDate()
    : next.endOf('day').toDate();
}

export function applyTimeToCalendarDate(
  date: Date | null,
  time: Date | null,
  emptyTimeDefault: DayBoundary
): Date | null {
  if (!date) return null;
  const base = dayjs(date);
  if (!time) {
    return emptyTimeDefault === 'start'
      ? base.startOf('day').toDate()
      : base.endOf('day').toDate();
  }
  const t = dayjs(time);
  return base
    .hour(t.hour())
    .minute(t.minute())
    .second(t.second())
    .millisecond(t.millisecond())
    .toDate();
}
